import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  useTheme,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import LinkIcon from '@mui/icons-material/Link';

interface SignatureEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
}

export const SignatureEditor: React.FC<SignatureEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter your signature...',
  maxLength = 10000,
  disabled = false,
}) => {
  const theme = useTheme();
  const editorRef = useRef<HTMLDivElement>(null);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
      setCharCount(value.length);
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      setCharCount(content.length);
      onChange(content);
    }
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      execCommand('insertHTML', '<br><br>');
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Signature Editor
      </Typography>
      
      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '4px 4px 0 0',
          p: 1,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => execCommand('bold')}
            disabled={disabled}
            sx={{ borderRadius: 1 }}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => execCommand('italic')}
            disabled={disabled}
            sx={{ borderRadius: 1 }}
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => execCommand('underline')}
            disabled={disabled}
            sx={{ borderRadius: 1 }}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <IconButton
            size="small"
            onClick={() => execCommand('foreColor', theme.palette.primary.main)}
            disabled={disabled}
            sx={{ borderRadius: 1 }}
          >
            <FormatColorTextIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              const url = prompt('Enter URL:');
              if (url) execCommand('createLink', url);
            }}
            disabled={disabled}
            sx={{ borderRadius: 1 }}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* Editor */}
      <Box
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          minHeight: 120,
          p: 2,
          fontSize: '14px',
          fontFamily: theme.typography.body1.fontFamily,
          outline: 'none',
          backgroundColor: disabled ? theme.palette.action.disabledBackground : theme.palette.background.paper,
          color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
          cursor: disabled ? 'not-allowed' : 'text',
          '&:focus': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
          },
          '&:empty::before': {
            content: `"${placeholder}"`,
            color: theme.palette.text.secondary,
            fontStyle: 'italic',
          },
        }}
        suppressContentEditableWarning={true}
      />

      {/* Character count */}
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Typography variant="caption" color="text.secondary">
          Use formatting tools to create professional signatures
        </Typography>
        <Typography 
          variant="caption" 
          color={charCount > maxLength * 0.9 ? 'error' : 'text.secondary'}
        >
          {charCount}/{maxLength}
        </Typography>
      </Box>
    </Box>
  );
};